/*
  House of Thiraa JavaScript foundation.
  This file exposes one deliberate namespace with shared helpers for future modules.
*/

(function () {
  'use strict';

  const selectors = {
    liveRegion: '[data-live-region]',
    focusable: [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[contenteditable]',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',')
  };

  const mediaQueries = {
    sm: '(min-width: 36rem)',
    md: '(min-width: 48rem)',
    lg: '(min-width: 64rem)',
    xl: '(min-width: 80rem)',
    motionSafe: '(prefers-reduced-motion: no-preference)',
    motionReduce: '(prefers-reduced-motion: reduce)'
  };

  class EventBus extends EventTarget {
    emit(type, detail = {}) {
      this.dispatchEvent(new CustomEvent(type, { detail }));
    }

    on(type, callback, options) {
      this.addEventListener(type, callback, options);
      return () => this.removeEventListener(type, callback, options);
    }
  }

  const events = new EventBus();

  const responsive = {
    query(name) {
      return window.matchMedia(mediaQueries[name] || name);
    },

    matches(name) {
      return this.query(name).matches;
    },

    onChange(name, callback) {
      const query = this.query(name);
      const handler = (event) => callback(event.matches, event);
      query.addEventListener('change', handler);
      callback(query.matches, query);
      return () => query.removeEventListener('change', handler);
    }
  };

  const a11y = {
    getFocusableElements(container = document) {
      return Array.from(container.querySelectorAll(selectors.focusable)).filter((element) => {
        return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
      });
    },

    announce(message, priority = 'polite') {
      const region = document.querySelector(`${selectors.liveRegion}[aria-live="${priority}"]`);

      if (!region || !message) {
        return;
      }

      region.textContent = '';
      window.requestAnimationFrame(() => {
        region.textContent = message;
      });
    },

    trapFocus(container, initialFocus = container) {
      const focusableElements = this.getFocusableElements(container);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const previousFocus = document.activeElement;

      const handleKeydown = (event) => {
        if (event.key !== 'Tab' || focusableElements.length === 0) {
          return;
        }

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }

        if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      };

      container.addEventListener('keydown', handleKeydown);
      (initialFocus || firstElement || container).focus({ preventScroll: true });

      return () => {
        container.removeEventListener('keydown', handleKeydown);

        if (previousFocus && typeof previousFocus.focus === 'function') {
          previousFocus.focus({ preventScroll: true });
        }
      };
    }
  };

  const lazy = {
    observe(elements, callback, options = {}) {
      const targets = Array.from(elements || []);

      if (!('IntersectionObserver' in window)) {
        targets.forEach((element) => callback(element));
        return () => {};
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          callback(entry.target, entry);
          observer.unobserve(entry.target);
        });
      }, {
        rootMargin: '200px 0px',
        threshold: 0.01,
        ...options
      });

      targets.forEach((element) => observer.observe(element));
      return () => observer.disconnect();
    },

    revealOnView(elements) {
      return this.observe(elements, (element) => {
        element.classList.add('is-visible');
      });
    }
  };

  const editor = {
    bindLifecycle() {
      const map = {
        'shopify:section:load': 'section:load',
        'shopify:section:unload': 'section:unload',
        'shopify:section:select': 'section:select',
        'shopify:section:deselect': 'section:deselect',
        'shopify:block:select': 'block:select',
        'shopify:block:deselect': 'block:deselect'
      };

      Object.entries(map).forEach(([shopifyEvent, themeEvent]) => {
        document.addEventListener(shopifyEvent, (event) => {
          events.emit(themeEvent, {
            sectionId: event.detail?.sectionId,
            blockId: event.detail?.blockId,
            target: event.target,
            originalEvent: event
          });
        });
      });
    }
  };

  const wishlist = (() => {
    const storageKey = 'houseOfThiraa:wishlist';

    const read = () => {
      try {
        const saved = JSON.parse(window.localStorage.getItem(storageKey) || '[]');

        if (Array.isArray(saved)) {
          return saved.reduce((items, savedItem) => {
            const id = typeof savedItem === 'object'
              ? String(savedItem.id || savedItem.productId || '')
              : String(savedItem || '');

            if (!id) {
              return items;
            }

            const handle = typeof savedItem === 'object'
              ? String(savedItem.handle || '')
              : '';

            items.set(id, { id, handle });
            return items;
          }, new Map());
        }
      } catch (error) {
        // Private browsing can prevent storage access.
      }

      return new Map();
    };

    let items = read();
    let ids = new Set(items.keys());

    const emitUpdated = () => {
      const productIds = Array.from(ids);
      const detail = { productIds };

      events.emit('wishlist:updated', detail);
      window.dispatchEvent(new CustomEvent('house-thiraa:wishlist-updated', { detail }));
    };

    const persist = () => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(
          Array.from(ids).map((id) => items.get(id) || { id, handle: '' })
        ));
      } catch (error) {
        // Keep the in-memory wishlist when storage is unavailable.
      }
    };

    window.addEventListener('storage', (event) => {
      if (event.key !== storageKey || event.storageArea !== window.localStorage) {
        return;
      }

      items = read();
      ids = new Set(items.keys());
      syncButtons(document);
      emitUpdated();
    });

    const syncButtons = (scope = document) => {
      const root = scope && scope.querySelectorAll ? scope : document;

      root.querySelectorAll('[data-featured-products-wishlist]').forEach((button) => {
        const isActive = ids.has(String(button.dataset.productId || ''));
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));

        if (button.dataset.addLabel && button.dataset.removeLabel) {
          button.setAttribute('aria-label', isActive ? button.dataset.removeLabel : button.dataset.addLabel);
        }
      });
    };

    return {
      ids() {
        return Array.from(ids);
      },
      items() {
        return Array.from(ids).map((id) => items.get(id) || { id, handle: '' });
      },
      has(productId) {
        return ids.has(String(productId || ''));
      },
      toggle(productId, productHandle = '') {
        const id = String(productId || '');

        if (!id) {
          return Array.from(ids);
        }

        if (ids.has(id)) {
          ids.delete(id);
          items.delete(id);
        } else {
          ids.add(id);
          items.set(id, { id, handle: String(productHandle || '') });
        }

        persist();
        syncButtons(document);
        emitUpdated();
        return Array.from(ids);
      },
      syncButtons
    };
  })();

  window.HouseOfThiraa = Object.freeze({
    events,
    responsive,
    a11y,
    lazy,
    editor,
    wishlist,
    selectors,
    mediaQueries
  });
})();
